import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import {
  IPluginDetailResponse,
  IUpdatePluginRequest,
} from '@/providers/HttpProvider/plugins/EnvioFlex/types';

import type { AddressDetails } from '@/components/address-autocomplete';
import type { MuiTelInputInfo } from 'mui-tel-input';

interface UseMercadoEnvíosConfigProps {
  pluginId: string;
  clientId: string;
  isOpen: boolean;
}

const normalizePhone = (phone: string) => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  return `+52 ${digits}`;
};

const PLATFORM_NAME = 'MercadoLibreFlex';

const useMercadoEnvíosConfig = ({ pluginId, clientId, isOpen }: UseMercadoEnvíosConfigProps) => {
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [checkOptions, setCheckOptions] = useState({ createOrders: true });
  const [storeInfo, setStoreInfo] = useState({
    storeName: '',
    storeEmail: '',
    storePhone: '',
    sellerId: '',
  });
  const [phoneError, setPhoneError] = useState(false);
  const [isWebhookPrimary, setIsWebhookPrimary] = useState(false);
  const [additionalInsurance, setAdditionalInsurance] = useState(false);
  const [insuranceLimit, setInsuranceLimit] = useState('');
  const [insuranceLimitError, setInsuranceLimitError] = useState(false);
  const [addressData, setAddressData] = useState({
    address: '',
    country: 'MEX',
    latitude: 0,
    longitude: 0,
    zipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleToggleAddress = () => setUseCustomAddress(!useCustomAddress);

  const handleStoreInfo = (
    field: 'storeName' | 'storeEmail' | 'storePhone' | 'sellerId',
    value: string,
  ) => {
    setStoreInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhone = (value: string, info: MuiTelInputInfo) => {
    setStoreInfo((prev) => ({ ...prev, storePhone: value }));
    const digits = (info.nationalNumber ?? '').replace(/\D/g, '');
    setPhoneError(digits.length > 0 && digits.length < 7);
  };

  const handleToggleAdditionalInsurance = () => {
    setAdditionalInsurance((prev) => {
      if (prev) setInsuranceLimitError(false);
      return !prev;
    });
  };

  const handleInsuranceLimit = (value: string) => {
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setInsuranceLimit(value);
    setInsuranceLimitError(value !== '' && Number(value) <= 0);
  };

  const handleCheckOptions = (name: 'createOrders') => {
    setCheckOptions((prev) => ({ ...prev, [name]: !checkOptions[name] }));
  };

  const handleSelectAddress = (details: AddressDetails) => {
    setAddressData({
      address: details.address,
      country: details.country || 'MEX',
      latitude: details.latitude,
      longitude: details.longitude,
      zipCode: details.zipCode,
    });
  };

  const handleChangeAddressText = (text: string) => {
    setAddressData((prev) => ({ ...prev, address: text }));
  };

  const loadPluginConfig = useCallback(async () => {
    if (!clientId || !pluginId) return;

    setLoading(true);
    setPhoneError(false);
    try {
      const http = initializeHttpProvider();
      const response = await http.envioFlex.getPluginDetails({ clientId, pluginId });

      if ('success' in response && response.success) {
        const { localConfig } = (response as IPluginDetailResponse).data;
        setUseCustomAddress(localConfig.useCollectAddress || false);
        setCheckOptions({ createOrders: localConfig.autoCreateOrder || false });
        setStoreInfo({
          storeName: localConfig.storeName || '',
          storeEmail: localConfig.storeEmail || '',
          storePhone: normalizePhone(localConfig.storePhone || ''),
          sellerId: localConfig.sellerId || '',
        });
        setIsWebhookPrimary(localConfig.webhookPlatform === PLATFORM_NAME);
        setAdditionalInsurance(localConfig.additionalInsurance || false);
        setInsuranceLimit(
          localConfig.additionalInsuranceLimit ? String(localConfig.additionalInsuranceLimit) : '',
        );

        if (localConfig.collectAddress) {
          setAddressData({
            address: localConfig.collectAddress.address || '',
            country: localConfig.collectAddress.country || 'MEX',
            latitude: localConfig.collectAddress.latitude || 0,
            longitude: localConfig.collectAddress.longitude || 0,
            zipCode: localConfig.collectAddress.zipCode || '',
          });
        }
      }
    } catch (error) {
      console.error('Error loading plugin config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, [clientId, pluginId]);

  const savePluginConfig = async () => {
    if (!clientId || !pluginId) {
      toast.error('Faltan datos requeridos');
      return;
    }
    if (phoneError) {
      toast.error('El teléfono de la tienda no es válido');
      return false;
    }
    if (additionalInsurance && (insuranceLimit === '' || Number(insuranceLimit) <= 0)) {
      setInsuranceLimitError(true);
      toast.error('Configura un monto límite válido para el seguro adicional');
      return false;
    }

    setSaving(true);
    try {
      const http = initializeHttpProvider();

      const requestData: IUpdatePluginRequest = {
        local_config: {
          autoCreateOrder: checkOptions.createOrders,
          useCollectAddress: useCustomAddress,
          collectAddress: {
            address: useCustomAddress ? addressData.address : '',
            country: useCustomAddress ? addressData.country : '',
            latitude: useCustomAddress ? addressData.latitude : 0,
            longitude: useCustomAddress ? addressData.longitude : 0,
            references: '',
            zipCode: useCustomAddress ? addressData.zipCode : '',
          },
          sellerId: storeInfo.sellerId,
          storeEmail: storeInfo.storeEmail,
          storeName: storeInfo.storeName,
          storePhone: storeInfo.storePhone,
          webhookPlatform: isWebhookPrimary ? PLATFORM_NAME : null,
          additionalInsurance,
          additionalInsuranceLimit: additionalInsurance ? Number(insuranceLimit) : null,
        },
      };

      const response = await http.envioFlex.updatePlugin({
        clientId,
        pluginId,
        data: requestData,
      });

      if ('success' in response && response.success) {
        toast.success('Configuración guardada exitosamente');
        return true;
      } else {
        toast.error('Error al guardar configuración');
        return false;
      }
    } catch (error) {
      console.error('Error saving plugin config:', error);
      toast.error('Error al guardar configuración');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPluginConfig();
    }
  }, [isOpen, pluginId, clientId, loadPluginConfig]);
  return {
    handleCheckOptions,
    checkOptions,
    handleSelectAddress,
    handleChangeAddressText,
    addressData,
    useCustomAddress,
    handleToggleAddress,
    storeInfo,
    handleStoreInfo,
    handlePhone,
    phoneError,
    isWebhookPrimary,
    setIsWebhookPrimary,
    additionalInsurance,
    handleToggleAdditionalInsurance,
    insuranceLimit,
    handleInsuranceLimit,
    insuranceLimitError,
    loading,
    saving,
    savePluginConfig,
  };
};

export default useMercadoEnvíosConfig;
